import { useEffect, useMemo, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import { GetExamSimulatorUsageCall } from "../../redux/api/examSimulator.api";

const headCellSx = {
  py: 1.5,
  px: 2,
  fontSize: "0.75rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "text.secondary",
  borderBottom: "1px solid",
  borderColor: "divider",
  bgcolor: "grey.50",
};

const bodyCellSx = {
  py: 1.75,
  px: 2,
  fontSize: "0.875rem",
  verticalAlign: "middle",
  borderColor: "divider",
};

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

function initialsFor(row) {
  const first = row.first_name?.charAt(0) || "";
  const last = row.last_name?.charAt(0) || "";
  const combo = `${first}${last}`.toUpperCase();
  if (combo) return combo;
  if (row.email) return row.email.charAt(0).toUpperCase();
  return "?";
}

export default function ExamSimulatorUsagePanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [summary, setSummary] = useState({
    total_sessions: 0,
    total_users: 0,
  });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, category]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    GetExamSimulatorUsageCall({
      page,
      limit,
      search: search || undefined,
      category: category || undefined,
    }).then((res) => {
      if (cancelled) return;
      if (res?.status === 200 && res.data) {
        setRows(Array.isArray(res.data.items) ? res.data.items : []);
        setMeta({
          total: res.data.pagination?.total || 0,
          totalPages: res.data.pagination?.totalPages || 1,
        });
        setSummary({
          total_sessions: res.data.summary?.total_sessions || 0,
          total_users: res.data.summary?.total_users || 0,
        });
      } else {
        setRows([]);
        setMeta({ total: 0, totalPages: 1 });
        setSummary({ total_sessions: 0, total_users: 0 });
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [page, limit, search, category, refreshTick]);

  const subtitle = useMemo(() => {
    if (loading) return "Loading…";
    if (meta.total === 0) return "No exam simulator users yet.";
    return `${summary.total_users} users · ${summary.total_sessions} sessions`;
  }, [loading, meta.total, summary]);

  return (
    <MDBox>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ md: "center" }}
        justifyContent="space-between"
        mb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Usage
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            label="Search user"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name or email"
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="histology">Histology</MenuItem>
              <MenuItem value="embryology">Embryology</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => setRefreshTick((n) => n + 1)}
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
        <Chip
          label={`${summary.total_users} users`}
          color="primary"
          variant="outlined"
        />
        <Chip
          label={`${summary.total_sessions} sessions`}
          color="secondary"
          variant="outlined"
        />
      </Stack>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>User</TableCell>
              <TableCell sx={headCellSx} align="right">
                Sessions
              </TableCell>
              <TableCell sx={headCellSx} align="right">
                Avg score
              </TableCell>
              <TableCell sx={headCellSx}>Platform</TableCell>
              <TableCell sx={headCellSx}>Country</TableCell>
              <TableCell sx={headCellSx}>Last exam</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">
                    No usage data for this filter.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.user_id} hover>
                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        src={row.profile_image || undefined}
                        sx={{ width: 36, height: 36 }}
                      >
                        {initialsFor(row)}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="text.primary"
                        >
                          {row.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.email || "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="right">
                    <Typography variant="body2" fontWeight={800}>
                      {row.session_count}
                    </Typography>
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="right">
                    {row.avg_score != null ? row.avg_score : "—"}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {row.platform || "—"}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {row.country || "—"}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {formatDate(row.last_finished_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={meta.total}
          page={Math.max(page - 1, 0)}
          onPageChange={(_e, zeroBased) => setPage(zeroBased + 1)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => {
            setLimit(parseInt(e.target.value, 10) || 25);
            setPage(1);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </MDBox>
  );
}
